import { useEffect, useState, type FormEvent } from "react";
import DatePicker from "@/components/base/DatePicker";
import { invokeAdmin } from "@/pages/backstage/lib/adminApi";
import BalineseHolidayPicker from "@/pages/backstage/components/BalineseHolidayPicker";

type TourBlockedDate = {
  id: string;
  date: string;
  reason: string | null;
  created_at: string;
};

interface TourBlockedDatesProps {
  pin: string | null;
  isDe: boolean;
}

export default function TourBlockedDates({ pin, isDe }: TourBlockedDatesProps) {
  const [items, setItems] = useState<TourBlockedDate[]>([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const today = new Date().toISOString().split("T")[0];

  const load = async () => {
    if (!pin) return;
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await invokeAdmin<{ blocked: TourBlockedDate[] }>({
        action: "list_tour_blocked",
        pin,
      });
      if (err) throw new Error(err);
      setItems(data?.blocked ?? []);
    } catch {
      setError(isDe ? "Laden fehlgeschlagen." : "Loading failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const handleBlock = async (e: FormEvent) => {
    e.preventDefault();
    if (!start) {
      setError(isDe ? "Bitte wählen Sie mindestens ein Von-Datum." : "Please choose at least a from date.");
      return;
    }
    const rangeEnd = end || start;
    if (rangeEnd < start) {
      setError(isDe ? "Das Bis-Datum liegt vor dem Von-Datum." : "The to date is before the from date.");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const { error: err } = await invokeAdmin<{ blocked: TourBlockedDate[] }>({
        action: "block_tour_dates",
        pin,
        start,
        end: rangeEnd,
        reason,
      });
      if (err) throw new Error(err);
      setNotice(
        isDe ? "Touren für diesen Zeitraum gesperrt." : "Tours blocked for this period."
      );
      setStart("");
      setEnd("");
      setReason("");
      await load();
    } catch {
      setError(isDe ? "Sperren fehlgeschlagen." : "Blocking failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleUnblock = async (id: string) => {
    setError("");
    setNotice("");
    try {
      const { error: err } = await invokeAdmin<{ ok?: boolean }>({
        action: "unblock_tour_id",
        pin,
        id,
      });
      if (err) throw new Error(err);
      setNotice(isDe ? "Tag wieder freigegeben." : "Day released again.");
      await load();
    } catch {
      setError(isDe ? "Freigeben fehlgeschlagen." : "Unblocking failed.");
    }
  };

  const formatDate = (iso: string) => {
    const parts = iso.slice(0, 10).split("-").map(Number);
    if (parts.length < 3) return iso;
    const d = new Date(parts[0], parts[1] - 1, parts[2]);
    return d.toLocaleDateString(isDe ? "de-DE" : "en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const todayNum = new Date(today).getTime();
  const upcoming = items.filter((it) => new Date(it.date.slice(0, 10)).getTime() >= todayNum);

  const inputClass =
    "w-full px-4 py-2.5 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-colors";

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-heading text-2xl text-foreground-950">
          {isDe ? "Tour-Kalender" : "Tour calendar"}
        </h2>
        <p className="text-sm text-foreground-600 mt-1">
          {isDe
            ? "Tage hier sperren, an denen keine Touren stattfinden. Die gesperrten Tage werden im Buchungsformular aller Touren ausgegraut und können nicht gewählt werden."
            : "Block days here on which no tours take place. Blocked days are greyed out in the booking form of all tours and cannot be selected."}
        </p>
      </div>

      {(notice || error) && (
        <div
          className={`mb-6 text-sm font-medium rounded-md px-4 py-3 border ${
            error
              ? "text-secondary-700 bg-secondary-100 border-secondary-200"
              : "text-primary-700 bg-primary-100 border-primary-200"
          }`}
        >
          {error || notice}
        </div>
      )}

      <BalineseHolidayPicker
        pin={pin}
        isDe={isDe}
        blockedDates={items.map((it) => String(it.date).slice(0, 10))}
        onSaved={load}
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1">
          <h3 className="font-heading text-xl text-foreground-950 mb-4">
            {isDe ? "Touren sperren" : "Block tours"}
          </h3>
          <form
            onSubmit={handleBlock}
            className="space-y-4 bg-background-50 border border-background-200 rounded-lg p-5"
          >
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                {isDe ? "Von" : "From"}
              </label>
              <DatePicker
                value={start}
                onChange={setStart}
                min={today}
                isDe={isDe}
                placeholder={isDe ? "Datum wählen" : "Select date"}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                {isDe ? "Bis (optional)" : "To (optional)"}
              </label>
              <DatePicker
                value={end}
                onChange={setEnd}
                min={start || today}
                isDe={isDe}
                placeholder={isDe ? "Einzelner Tag" : "Single day"}
              />
              <p className="mt-1.5 text-xs text-foreground-500">
                {isDe
                  ? "Leer lassen, um nur einen einzelnen Tag zu sperren."
                  : "Leave empty to block a single day only."}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-800 mb-1.5">
                {isDe ? "Grund (optional)" : "Reason (optional)"}
              </label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={200}
                placeholder={isDe ? "z. B. Feiertag / Ruhetag" : "e.g. public holiday / day off"}
                className={inputClass}
              />
            </div>
            <button
              type="submit"
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 disabled:opacity-60 text-background-50 text-sm font-medium px-6 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
            >
              {saving ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-calendar-close-line"></i>
              )}
              {isDe ? "Tage sperren" : "Block days"}
            </button>
          </form>
        </div>

        <div className="lg:col-span-2">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h3 className="font-heading text-xl text-foreground-950">
              {isDe ? "Gesperrte Tour-Tage" : "Blocked tour days"}
            </h3>
            <span className="text-xs bg-accent-100 text-accent-800 px-2.5 py-1 rounded-full whitespace-nowrap">
              {isDe
                ? `${upcoming.length} kommend / ${items.length} gesamt`
                : `${upcoming.length} upcoming / ${items.length} total`}
            </span>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-foreground-600 py-6">
              <i className="ri-loader-4-line animate-spin"></i>
              {isDe ? "Wird geladen..." : "Loading..."}
            </div>
          ) : items.length === 0 ? (
            <div className="bg-background-100 border border-background-200 rounded-lg p-10 text-center text-foreground-600">
              {isDe
                ? "Noch keine gesperrten Tour-Tage."
                : "No blocked tour days yet."}
            </div>
          ) : (
            <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
              <div className="max-h-[560px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-background-100 text-foreground-700 text-left sticky top-0">
                    <tr>
                      <th className="px-4 py-3 font-medium">{isDe ? "Datum" : "Date"}</th>
                      <th className="px-4 py-3 font-medium">{isDe ? "Grund" : "Reason"}</th>
                      <th className="px-4 py-3 font-medium text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => {
                      const isPast = new Date(it.date.slice(0, 10)).getTime() < todayNum;
                      return (
                        <tr key={it.id} className="border-t border-background-100">
                          <td className="px-4 py-2.5">
                            <span className={isPast ? "text-foreground-400" : "text-foreground-800"}>
                              {formatDate(it.date)}
                            </span>
                            {isPast && (
                              <span className="ml-2 text-[10px] uppercase tracking-wide text-foreground-400">
                                {isDe ? "vorbei" : "past"}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2.5 text-foreground-600">
                            {it.reason || "—"}
                          </td>
                          <td className="px-4 py-2.5 text-right">
                            <button
                              onClick={() => handleUnblock(it.id)}
                              className="inline-flex items-center gap-1 text-xs text-secondary-700 bg-secondary-100 hover:bg-secondary-200 border border-secondary-200 px-2.5 py-1.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
                            >
                              <i className="ri-calendar-check-line"></i>
                              {isDe ? "Freigeben" : "Unblock"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}